import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Clock, PartyPopper, Loader2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PhysicalInterest } from "../components/PhysicalInterest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 10;

export default function SuccessPage() {
  const [params] = useSearchParams();
  const identifier = params.get("order") || params.get("order_identifier");
  const [state, setState] = useState(identifier ? "checking" : "no_order");
  const [downloads, setDownloads] = useState([]);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const polls = useRef(0);

  const startDownload = (key) => {
    setDownloadingKey(key);
    // Direct navigation -> backend verifies the order and answers with a
    // 302 redirect to the exact fresh signed Lemon Squeezy download URL.
    window.location.href = `${API}/orders/${encodeURIComponent(identifier)}/download/${key}`;
    setTimeout(() => setDownloadingKey(null), 4000);
  };

  useEffect(() => {
    if (params.get("dl_error")) {
      toast.error("Download derzeit nicht verfügbar. Bitte nutze den Link in deiner Bestell-E-Mail oder kontaktiere hallo@visualma-lernwelten.de.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!identifier) return;
    let timer;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${encodeURIComponent(identifier)}`);
        if (data.payment_status === "paid") {
          setDownloads(data.downloads || []);
          setState("paid");
          return;
        }
      } catch {}
      polls.current += 1;
      if (polls.current >= MAX_POLLS) setState("still_pending");
      else timer = setTimeout(check, 3000);
    };
    check();
    return () => clearTimeout(timer);
  }, [identifier]);

  return (
    <main className="pt-28 pb-24 min-h-[75vh]" data-testid="success-page">
      <div className="max-w-2xl mx-auto px-5">
        {state === "no_order" && (
          <Reveal className="card-soft p-10 text-center" data-testid="success-no-order">
            <h1 className="font-display text-3xl font-medium mb-3">Keine Bestellung gefunden</h1>
            <p className="text-sm text-[color:var(--ink-muted)] leading-relaxed mb-8">
              Diese Seite wird nach einem erfolgreichen Kauf automatisch aufgerufen. Es wurde keine
              Bestellnummer übergeben.
            </p>
            <Link to="/lernwelten/farben" className="btn-primary">Zum Lernpaket</Link>
          </Reveal>
        )}

        {state === "checking" && (
          <div className="card-soft p-10 text-center" data-testid="success-checking">
            <Clock size={40} className="mx-auto mb-5 animate-pulse" style={{ color: "var(--terra)" }} />
            <h1 className="font-display text-3xl font-medium mb-3">Deine Bestellung wird bestätigt…</h1>
            <p className="text-sm text-[color:var(--ink-muted)] leading-relaxed">
              Das dauert in der Regel nur wenige Sekunden.
            </p>
          </div>
        )}

        {state === "still_pending" && (
          <div className="card-soft p-10 text-center" data-testid="success-pending">
            <Clock size={40} className="mx-auto mb-5" style={{ color: "var(--gelb)" }} />
            <h1 className="font-display text-3xl font-medium mb-3">Bestätigung dauert etwas länger</h1>
            <p className="text-sm text-[color:var(--ink-muted)] leading-relaxed mb-8">
              Deine Zahlung wird noch verarbeitet. Bitte lade diese Seite in ein bis zwei Minuten
              neu. Du erhältst außerdem eine Bestell-E-Mail. Bei Fragen:{" "}
              <a href="mailto:hallo@visualma-lernwelten.de" className="underline font-medium">hallo@visualma-lernwelten.de</a>
            </p>
            <button className="btn-ghost" onClick={() => window.location.reload()} data-testid="success-reload-button">
              Seite neu laden
            </button>
          </div>
        )}

        {state === "paid" && (
          <>
            <Reveal className="text-center mb-12">
              <PartyPopper size={44} className="mx-auto mb-6" style={{ color: "var(--terra)" }} />
              <h1 className="font-display font-medium tracking-tight text-4xl sm:text-5xl leading-[1.08] mb-4" data-testid="success-headline">
                Willkommen in deiner VisualMA <em style={{ color: "var(--terra)" }}>Lernwelt.</em>
              </h1>
              <p className="text-[15px] sm:text-base text-[color:var(--ink-muted)]">
                Dein Lernpaket „Kleine Welt, große Farben" ist bereit.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="space-y-3 mb-12">
              {downloads.map((d) => (
                <div key={d.key} className="card-soft !rounded-2xl p-5 flex items-center gap-4" data-testid={`download-${d.key}`}>
                  <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FDF2EF", color: "var(--terra)" }}>
                    <Download size={19} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[15px] leading-snug">{d.title}</p>
                    {d.subtitle && (
                      <p className="text-[12px] font-medium mt-0.5" style={{ color: "var(--terra)" }}>{d.subtitle}</p>
                    )}
                    {d.desc && (
                      <p className="text-xs text-[color:var(--ink-muted)] mt-1 leading-relaxed">{d.desc}</p>
                    )}
                    {!d.available && (
                      <p className="text-xs text-[color:var(--ink-muted)] mt-1">
                        Wird gerade für dich bereitgestellt – du erhältst den Download auch per Bestell-E-Mail.
                      </p>
                    )}
                  </div>
                  {d.available ? (
                    <button
                      onClick={() => startDownload(d.key)}
                      disabled={downloadingKey === d.key}
                      className="btn-primary !py-2.5 !px-5 !text-[13px] shrink-0"
                      data-testid={`download-link-${d.key}`}
                    >
                      {downloadingKey === d.key ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Wird geladen…
                        </span>
                      ) : (
                        "Herunterladen"
                      )}
                    </button>
                  ) : (
                    <span className="text-[11px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full shrink-0" style={{ background: "#FCF7EB", color: "#A97516" }}>
                      In Kürze
                    </span>
                  )}
                </div>
              ))}
              <p className="text-xs text-[color:var(--ink-muted)] pt-2 text-center" data-testid="success-email-note">
                Du erhältst deinen Zugang zusätzlich per Bestell-E-Mail und kannst deine aktuell
                verfügbaren Dateien später erneut über{" "}
                <a href="https://app.lemonsqueezy.com/my-orders" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                  Lemon Squeezy My Orders
                </a>{" "}
                aufrufen.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="card-soft p-8" data-testid="success-interest">
              <p className="font-display text-xl font-medium mb-1.5">VisualMA bald auch zum Anfassen?</p>
              <p className="text-sm text-[color:var(--ink-muted)] mb-6">
                Du hast jetzt die digitale Lernwelt. Hättest du zusätzlich Interesse an einer
                hochwertigen physischen Ausgabe?
              </p>
              <PhysicalInterest source="post_purchase" compact yesLabel="Ja, unbedingt" />
            </Reveal>
          </>
        )}
      </div>
    </main>
  );
}
